/**
 * Which style this organisation uses, and who holds which face.
 *
 * Context rather than props: `Face` has seventeen call sites across seven
 * files, one of which is the marketing page — which has no organisation and
 * never will. The default below is what that page renders: `patterns`, the one
 * style drawn in CSS, so a page with no organisation behind it needs no
 * vendored assets to draw a face.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react'

import { FALLBACK_FACE_STYLE, type FaceSetView } from './faces'

export interface FaceContextValue {
  /**
   * One of the five built-ins, or `set:<slug>` naming the organisation's own
   * pool. A plain string rather than `FaceStyle`, because that union cannot
   * hold a set. `faceArt` is what tells the two apart.
   */
  style: string
  /** Agent name to `avatar_key`. A name that is absent has no face yet. */
  faces: Record<string, string | null>
  /** The pool `style` names, expanded, or null where there is none to draw. */
  set: FaceSetView | null
}

const FaceContext = createContext<FaceContextValue>({
  style: FALLBACK_FACE_STYLE,
  faces: {},
  set: null,
})

export function useFaceContext(): FaceContextValue {
  return useContext(FaceContext)
}

export function FaceProvider({ style, faces, set = null, children }: Omit<FaceContextValue, 'set'> & {
  set?: FaceSetView | null
  children: ReactNode
}) {
  const value = useMemo(() => ({ style, faces, set }), [style, faces, set])
  return <FaceContext.Provider value={value}>{children}</FaceContext.Provider>
}
